import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ON_EXECUTE } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import {
  AntimalwareOnExecuteRansomwareRemediationRestoreMode,
  AntimalwareOnExecuteVirusControlDefaultAction,
  AntimalwareOnExecuteVirusControlProfile,
  ModuleId,
  SectionId,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled, toggleVisibility } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  AntimalwareOnExecuteFormValue,
  AntimalwareOnExecuteSearchableKeywords,
} from '../../../../utils/uiSettings/antimalware/antimalware.types';
import {
  getAntimalwareOnExecuteDefaultValue,
  getAntimalwareOnExecuteSearchableKeywords,
  getAntimalwareOnExecuteSensitiveRegistryProtectionActionFormOptions,
  getAntimalwareOnExecuteVirusControlDefaultActionFormOptions,
} from '../../../../utils/uiSettings/antimalware/antimalware.values';
import { FontSizes, FontWeights, LabelWidthValues } from '../../../../../../../types/common/types';
import { GzSelectionCardGroupWebComponent } from '../../../../../../../ui/gzSelectionCardGroup';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { GzDropdownWebComponent } from '../../../../../../../ui/gzDropdown';
import { InfoBoxSeverity } from '../../../../../../../ui/gzInfoBox/types';
import { GzTextWebComponent } from '../../../../../../../ui/gzText';
import { GzInfoBoxWebComponent } from '../../../../../../../ui/gzInfoBox';
import { GzRadioGroupWebComponent } from '../../../../../../../ui/gzRadio';
import { Feature } from '@nirvana/shared/constants';
import { classMap } from 'lit/directives/class-map.js';
import { SectionMask } from '../../../../redux/types';

@customElement(WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ON_EXECUTE)
export class GzAntimalwareOnExecuteWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  AntimalwareOnExecuteFormValue,
  AntimalwareOnExecuteSearchableKeywords
> {
  protected path = {
    moduleId: ModuleId.ANTIMALWARE,
    sectionId: SectionId.ANTIMALWARE_ON_EXECUTE,
  };
  protected searchableKeywords = getAntimalwareOnExecuteSearchableKeywords();

  // theta
  @query('#antimalware-on-execute-theta-enable-field')
  private onExecuteThetaEnableField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#antimalware-on-execute-theta-info-text')
  private onExecuteVirusControlInfoText: GzTextWebComponent;

  // virusControl
  @query('#antimalware-on-execute-virus-control-enable-field')
  private onExecuteVirusControlEnableField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#antimalware-on-execute-virus-control-content')
  private onExecuteVirusControlContent: HTMLElement;
  @query('#antimalware-on-execute-sensitive-protection-enable-field')
  private onExecuteSensitiveProtectionEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#antimalware-on-execute-sensitive-protection-action-field')
  private onExecuteSensitiveProtectionActionField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#antimalware-on-execute-virus-control-default-action-field')
  private onExecuteVirusControlDefaultActionField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#antimalware-on-execute-virus-control-profile-field')
  private onExecuteVirusControlProfileField: GzFormFieldWebComponent<GzSelectionCardGroupWebComponent>;
  @query('#antimalware-on-execute-kernel-api-enable-field')
  private onExecuteKernelApiEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  // commandLineScanning
  @query('#antimalware-on-execute-command-line-fileless-enable-field')
  private onExecuteCommandLineFilelessEnableField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#antimalware-on-execute-command-line-info-text')
  private onExecuteCommandLineInfoText: GzTextWebComponent;
  @query('#antimalware-on-execute-command-line-info-box')
  private onExecuteCommandLineInfoBox: GzInfoBoxWebComponent;
  @query('#antimalware-on-execute-command-line-content')
  private onExecuteCommandLineContent: HTMLElement;
  @query('#antimalware-on-execute-command-line-enable-field')
  private onExecuteCommandLineEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#antimalware-on-execute-command-line-amsi-enable-field')
  private onExecuteCommandLineAmsiEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#antimalware-on-execute-command-line-amsi-report-field')
  private onExecuteCommandLineAmsiReportField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  // ransomwareRemediation
  @query('#antimalware-on-execute-ransomware-enable-field')
  private onExecuteRansomwareEnableField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#antimalware-on-execute-ransomware-info-text')
  private onExecuteRansomwareInfoText: GzTextWebComponent;
  @query('#antimalware-on-execute-ransomware-info-box')
  private onExecuteRansomwareInfoBox: GzInfoBoxWebComponent;
  @query('#antimalware-on-execute-ransomware-content')
  private onExecuteRansomwareContent: HTMLElement;
  @query('#antimalware-on-execute-ransomware-efs-enable-field')
  private onExecuteRansomwareEfsEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#antimalware-on-execute-ransomware-local-attack-enable-field')
  private onExecuteRansomwareLocalAttackEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#antimalware-on-execute-ransomware-remote-attack-enable-field')
  private onExecuteRansomwareRemoteAttackEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#antimalware-on-execute-ransomware-restore-mode-field')
  private onExecuteRansomwareRestoreModeField: GzFormFieldWebComponent<GzRadioGroupWebComponent>;

  private isCompanionStandalone = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE]);
  private isEdrVisible = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_EDR_VISIBLE]);
  private isFilelessAttackVisible = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_FILELESS_ATTACK_VISIBLE]);
  private isRansomwareRemediationVisible = Application.Main.hasFeatures([
    Feature.PRODUCT_FEATURE_RANSOMWARE_REMEDIATION_VISIBLE,
  ]);

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  protected setMask(masks: SectionMask[]) {
    // for antimalware on access mask, only a part from this section is disabled
    super.setMask(this.masks.filter(mask => mask !== SectionMask.ANTIMALWARE_ON_ACCESS));
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-title
      class="content"
      searchable-label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.OnExecuteScanning].label}"
      ><span>${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.OnExecuteScanning].translatedLabel}</span>
    </gz-title>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}" class="infoText"
      >${translateFn(
        'On-execute Scanning blocks malware by analyzing files and applications as they are launched, preventing malicious code from executing.'
      )}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div id="antimalware-on-execute-content">
        <!-- On execute - Cloud based threat detection -->
        <div id="antimalware-on-execute-theta-section" class="mt-4 ${classMap({ hidden: !this.isEdrVisible })}">
          <gz-form-field id="antimalware-on-execute-theta-enable-field" class="mbw-1" status-field>
            <gz-toggle
              class="content-title-toggle"
              searchable-label="${this.searchableKeywords[
                AntimalwareOnExecuteSearchableKeywords.CloudBasedThreatDetection
              ].label}"
              gz-form-input
              @toggleChange="${e => toggleDisabled(this.onExecuteVirusControlInfoText, !e.detail.checked)}"
            >
              <span
                >${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.CloudBasedThreatDetection]
                  .translatedLabel}</span
              >
            </gz-toggle>
          </gz-form-field>

          <gz-text
            id="antimalware-on-execute-theta-info-text"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText"
            >${translateFn(
              'Cloud-based Threat Detection leverages machine learning to identify advanced threats. This approach optimizes resource usage by reducing local disk footprint and system requirements.'
            )}
          </gz-text>
        </div>

        <!-- On execute - Advanced Threat Control -->
        <div id="antimalware-on-execute-virus-control-section" class="mt-4">
          <gz-form-field id="antimalware-on-execute-virus-control-enable-field" class="mbw-0" status-field>
            <gz-toggle
              class="content-title-toggle"
              searchable-label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.AdvancedThreatControl]
                .label}"
              gz-form-input
              @toggleChange="${this.onExecuteVirusControlToggleChange}"
            >
              <span
                >${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.AdvancedThreatControl]
                  .translatedLabel}</span
              >
            </gz-toggle>
          </gz-form-field>

          <div id="antimalware-on-execute-virus-control-content" class="mt-2">
            <gz-form-field
              class="mt-2 ${classMap({ disabled: this.isCompanionStandalone })}"
              id="antimalware-on-execute-virus-control-default-action-field"
              searchable-label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.DefaultAction].label}"
              label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.DefaultAction].translatedLabel}"
            >
              <gz-dropdown
                gz-form-input
                labelWidth="${LabelWidthValues.MEDIUM}"
                .options="${getAntimalwareOnExecuteVirusControlDefaultActionFormOptions()}"
              ></gz-dropdown>
            </gz-form-field>

            <gz-form-field id="antimalware-on-execute-virus-control-profile-field" class="mt-3">
              <gz-selection-card-group gz-form-input>
                <gz-selection-card
                  searchable-label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Aggressive].label}"
                  label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Aggressive].translatedLabel}"
                  .value="${AntimalwareOnExecuteVirusControlProfile.AGGRESSIVE}"
                >
                  <div slot="content">
                    <gz-title
                      fontSize="${FontSizes.PARAGRAPH_12}"
                      fontWeight="${FontWeights.SEMIBOLD}"
                      style="margin-bottom: 8px"
                      >${translateFn('Recommended for critical systems')}</gz-title
                    >
                    <gz-text fontSize="${FontSizes.PARAGRAPH_12}"
                      >${translateFn(
                        'This option maximizes threat detection, potentially increasing the number of false positives.'
                      )}</gz-text
                    >
                  </div>
                </gz-selection-card>
                <gz-selection-card
                  searchable-label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Normal].label}"
                  label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Normal].translatedLabel}"
                  .value="${AntimalwareOnExecuteVirusControlProfile.NORMAL}"
                >
                  <div slot="content">
                    <gz-title
                      fontSize="${FontSizes.PARAGRAPH_12}"
                      fontWeight="${FontWeights.SEMIBOLD}"
                      style="margin-bottom: 8px"
                      >${translateFn('Recommended for most systems')}</gz-title
                    >
                    <gz-text fontSize="${FontSizes.PARAGRAPH_12}"
                      >${translateFn(
                        'This option balances detection sensitivity with the risk of false positives.'
                      )}</gz-text
                    >
                  </div>
                </gz-selection-card>
                <gz-selection-card
                  searchable-label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Permissive].label}"
                  label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Permissive].translatedLabel}"
                  .value="${AntimalwareOnExecuteVirusControlProfile.PERMISSIVE}"
                >
                  <div slot="content">
                    <gz-title
                      fontSize="${FontSizes.PARAGRAPH_12}"
                      fontWeight="${FontWeights.SEMIBOLD}"
                      style="margin-bottom: 8px"
                      >${translateFn('Not recommended')}</gz-title
                    >
                    <gz-text fontSize="${FontSizes.PARAGRAPH_12}"
                      >${translateFn(
                        "This option minimizes false positives by lowering Advanced Threat Control's detection sensitivity. "
                      )}</gz-text
                    >
                  </div>
                </gz-selection-card>
              </gz-selection-card-group>
            </gz-form-field>

            <div class="checkbox-with-input">
              <gz-form-field class="checkbox-width mbw-1" id="antimalware-on-execute-sensitive-protection-enable-field">
                <gz-checkbox
                  searchable-label="${this.searchableKeywords[
                    AntimalwareOnExecuteSearchableKeywords.SensitiveProtection
                  ].label}"
                  label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.SensitiveProtection]
                    .translatedLabel}"
                  gz-form-input
                  @change="${e => toggleDisabled(this.onExecuteSensitiveProtectionActionField, !e.detail.checked)}"
                ></gz-checkbox>
                <gz-input-error></gz-input-error>
              </gz-form-field>

              <gz-form-field id="antimalware-on-execute-sensitive-protection-action-field" class="ml-2 mbw-1">
                <gz-dropdown
                  gz-form-input
                  labelWidth="${LabelWidthValues.MEDIUM}"
                  .options="${getAntimalwareOnExecuteSensitiveRegistryProtectionActionFormOptions()}"
                ></gz-dropdown>
              </gz-form-field>
            </div>
            <gz-text
              id="antimalware-on-execute-sensitive-protection-info-text"
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
              class="infoText helper-text"
              >${translateFn(
                'Protects critical registry keys, including those associated with Security Account Manager, from unauthorized access or exploitation.'
              )}
            </gz-text>

            <gz-form-field id="antimalware-on-execute-kernel-api-enable-field" class="mt-2 mbw-1">
              <gz-checkbox
                searchable-label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.KernelApi].label}"
                label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.KernelApi].translatedLabel}"
                gz-form-input
              ></gz-checkbox>
            </gz-form-field>
            <gz-text
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
              class="infoText helper-text"
              >${translateFn(
                "This opt-in feature enables ATC's sensors to monitor Windows API calls at the kernel level, enhancing ATC's ability to detect advanced threats early in the attack chain."
              )}
            </gz-text>
          </div>
        </div>

        <!-- On execute - Fileless Attack Protection -->
        <div
          id="antimalware-on-execute-command-line-section"
          class="mt-4 ${classMap({ hidden: !this.isFilelessAttackVisible })}"
        >
          <gz-form-field id="antimalware-on-execute-command-line-fileless-enable-field" class="mbw-1" status-field>
            <gz-toggle
              class="content-title-toggle"
              searchable-label="${this.searchableKeywords[
                AntimalwareOnExecuteSearchableKeywords.FilelessAttackProtection
              ].label}"
              gz-form-input
              @toggleChange="${this.checkFilelessAttackProtectionStateForFileless}"
            >
              <span
                >${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.FilelessAttackProtection]
                  .translatedLabel}</span
              >
            </gz-toggle>
          </gz-form-field>

          <gz-text
            id="antimalware-on-execute-command-line-info-text"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText"
            >${this.isCompanionStandalone
              ? translateFn(
                  'Fileless Attack Protection enables GravityZone to proactively detect fileless attacks before they execute.'
                )
              : translateFn(
                  'Fileless Attack Protection enables GravityZone to proactively detect and block fileless attacks before they execute.'
                )}
          </gz-text>

          <gz-info-box
            id="antimalware-on-execute-command-line-info-box"
            severity="${InfoBoxSeverity.WARNING}"
            class="hidden mt-2"
          >
            ${translateFn(
              'Fileless Attack Protection is unavailable because the On-access Scanning module is disabled.'
            )}
          </gz-info-box>

          <div id="antimalware-on-execute-command-line-content" class="mt-2">
            <gz-form-field id="antimalware-on-execute-command-line-enable-field">
              <gz-checkbox
                searchable-label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.CommandLineScanner]
                  .label}"
                label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.CommandLineScanner]
                  .translatedLabel}"
                gz-form-input
                @change="${this.checkFilelessAttackProtectionStateForEnable.bind(this)}"
              ></gz-checkbox>
              <gz-input-error></gz-input-error>
            </gz-form-field>

            <gz-form-field
              id="antimalware-on-execute-command-line-amsi-enable-field"
              class="${classMap({ hidden: this.isCompanionStandalone })}"
            >
              <gz-checkbox
                searchable-label="${this.searchableKeywords[
                  AntimalwareOnExecuteSearchableKeywords.AntimalwareScanInterface
                ].label}"
                label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.AntimalwareScanInterface]
                  .translatedLabel}"
                gz-form-input
                @change="${this.checkFilelessAttackProtectionStateForAmsiEnable.bind(this)}"
              ></gz-checkbox>
              <gz-input-error></gz-input-error>
            </gz-form-field>

            <gz-form-field
              id="antimalware-on-execute-command-line-amsi-report-field"
              class="helper-text ${classMap({ hidden: this.isCompanionStandalone })}"
            >
              <gz-checkbox
                searchable-label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Report].label}"
                label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Report].translatedLabel}"
                gz-form-input
                @change="${this.checkFilelessAttackProtectionStateForAmsiReportEnable.bind(this)}"
              ></gz-checkbox>
              <gz-input-error></gz-input-error>
            </gz-form-field>
          </div>
        </div>

        <!-- On execute - Ransomware Mitigation -->
        <div
          id="antimalware-on-execute-ransomware-section"
          class="mt-4 ${classMap({ hidden: this.isCompanionStandalone || !this.isRansomwareRemediationVisible })}"
        >
          <gz-form-field id="antimalware-on-execute-ransomware-enable-field" class="mbw-1" status-field>
            <gz-toggle
              class="content-title-toggle"
              searchable-label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Ransomware].label}"
              gz-form-input
              @toggleChange="${this.checkRansomwareMitigationStateForEnable.bind(this)}"
            >
              <span>${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Ransomware].translatedLabel}</span>
            </gz-toggle>
          </gz-form-field>

          <gz-text
            id="antimalware-on-execute-ransomware-info-text"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText"
            >${translateFn(
              'Ransomware Mitigation recovers files encrypted by ransomware, immediately after the attack is detected and blocked.'
            )}
          </gz-text>

          <gz-info-box
            id="antimalware-on-execute-ransomware-info-box"
            severity="${InfoBoxSeverity.WARNING}"
            class="hidden mt-2"
          >
            ${translateFn(
              'Ransomware Mitigation is unavailable because the Advanced Threat Control module is disabled.'
            )}
          </gz-info-box>

          <div id="antimalware-on-execute-ransomware-content">
            <gz-form-field id="antimalware-on-execute-ransomware-efs-enable-field" class="mt-2 mbw-1">
              <gz-checkbox
                searchable-label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Efs].label}"
                label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Efs].translatedLabel}"
                gz-form-input
              ></gz-checkbox>
            </gz-form-field>
            <gz-text
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
              class="infoText helper-text"
              >${translateFn(
                'EFS Protection secures Windows Encrypting File System data against unauthorized access, tampering, and ransomware.'
              )}
            </gz-text>

            <gz-title
              fontWeight="${FontWeights.SEMIBOLD}"
              fontSize="${FontSizes.PARAGRAPH_12}"
              class="mt-3 subtitle"
              searchable-label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Monitor].label}"
              >${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Monitor].translatedLabel}</gz-title
            >

            <gz-form-field id="antimalware-on-execute-ransomware-local-attack-enable-field" class="mt-2 mbw-1">
              <gz-checkbox
                searchable-label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Locally].label}"
                label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Locally].translatedLabel}"
                gz-form-input
                @change="${this.checkRansomwareMitigationStateForAttack}"
              ></gz-checkbox>
            </gz-form-field>
            <gz-text
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
              class="infoText helper-text"
              >${translateFn(
                'Monitors processes running directly on the endpoint. Recommended for workstations. Use with caution on servers due to potential performance impact.'
              )}
            </gz-text>

            <gz-form-field id="antimalware-on-execute-ransomware-remote-attack-enable-field" class="mt-2 mbw-1">
              <gz-checkbox
                searchable-label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Remote].label}"
                label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Remote].translatedLabel}"
                gz-form-input
                @change="${this.checkRansomwareMitigationStateForAttack}"
              ></gz-checkbox>
            </gz-form-field>
            <gz-text
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
              class="infoText helper-text"
              >${translateFn(
                'Monitors network shares that are accessed remotely. Use this option if the endpoint is a file server or has network shares enabled.'
              )}
            </gz-text>

            <gz-title
              fontWeight="${FontWeights.SEMIBOLD}"
              fontSize="${FontSizes.PARAGRAPH_12}"
              class="mt-3 subtitle"
              searchable-label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Recover].label}"
              >${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Recover].translatedLabel}</gz-title
            >

            <gz-form-field id="antimalware-on-execute-ransomware-restore-mode-field" class="mt-2">
              <gz-radio-group gz-form-input>
                <gz-radio
                  searchable-label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.OnDemand].label}"
                  label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.OnDemand].translatedLabel}"
                  .value="${AntimalwareOnExecuteRansomwareRemediationRestoreMode.ON_DEMAND}"
                ></gz-radio>
                <gz-text
                  fontSize="${FontSizes.PARAGRAPH_12}"
                  fontWeight="${FontWeights.NORMAL}"
                  class="infoText helper-text"
                  .innerHTML=${translateFn(
                    'Allows you to manually initiate file recovery from the {bold}Ransomware activity{/bold} page.',
                    {
                      bold: '<strong>',
                      '/bold': '</strong>',
                    }
                  )}
                >
                </gz-text>

                <gz-radio
                  class="mt-1"
                  searchable-label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Automatically]
                    .label}"
                  label="${this.searchableKeywords[AntimalwareOnExecuteSearchableKeywords.Automatically]
                    .translatedLabel}"
                  .value="${AntimalwareOnExecuteRansomwareRemediationRestoreMode.AUTOMATIC}"
                ></gz-radio>
                <gz-text
                  fontSize="${FontSizes.PARAGRAPH_12}"
                  fontWeight="${FontWeights.NORMAL}"
                  class="infoText helper-text"
                  >${translateFn('Automatically recovers files after a ransomware attack is detected.')}
                </gz-text>
              </gz-radio-group>
            </gz-form-field>
          </div>
        </div>
      </div>
    `;
  }

  private onExecuteVirusControlToggleChange() {
    toggleDisabled(this.onExecuteVirusControlContent, !this.onExecuteVirusControlEnableField.value);

    if (this.isRansomwareRemediationVisible) {
      this.toggleRansomwareRemediationDisabledState();
    }
  }

  private checkFilelessAttackProtectionStateForFileless(event: CustomEvent) {
    const value = event.detail.checked;
    this.toggleOnExecuteFilelessDisabledState();

    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    this.onExecuteCommandLineEnableField.value = value;
    this.onExecuteCommandLineAmsiEnableField.value = false;
    this.onExecuteCommandLineAmsiReportField.value = false;
  }

  private checkFilelessAttackProtectionStateForEnable() {
    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    this.checkFilelessAttackProtectionState();
  }

  private checkFilelessAttackProtectionStateForAmsiEnable(event: CustomEvent) {
    const checked = event.detail.checked;
    toggleDisabled(this.onExecuteCommandLineAmsiReportField, !checked);

    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    if (!checked) {
      this.onExecuteCommandLineAmsiReportField.value = false;
    }

    this.checkFilelessAttackProtectionState();
  }

  private checkFilelessAttackProtectionStateForAmsiReportEnable() {
    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    this.checkFilelessAttackProtectionState();
  }

  private checkFilelessAttackProtectionState() {
    const isEnabled = this.onExecuteCommandLineFilelessEnableField.value;
    const isCmdEnabled = this.onExecuteCommandLineEnableField.value;
    const isCmdAmsiEnabled = this.onExecuteCommandLineAmsiEnableField.value;
    const isCmdAmsiReportEnabled = this.onExecuteCommandLineAmsiReportField.value;

    if (isCmdEnabled || isCmdAmsiEnabled || isCmdAmsiReportEnabled) {
      return;
    }

    if (!isEnabled) {
      return;
    }

    this.onExecuteCommandLineFilelessEnableField.value = false;
  }

  private checkRansomwareMitigationStateForEnable(event: CustomEvent) {
    const value = event.detail.checked;
    this.toggleRansomwareRemediationDisabledState();

    // wait for component to be ready
    // nothing to do when ransomware becomes disabled
    if (!this.isReady || !value) {
      return;
    }

    // if ransomware is enabled, then enable local attack by default if no attack is enabled
    const localAttackValue = this.onExecuteRansomwareLocalAttackEnableField.value;
    const remoteAttackValue = this.onExecuteRansomwareRemoteAttackEnableField.value;
    if (!localAttackValue && !remoteAttackValue) {
      this.onExecuteRansomwareLocalAttackEnableField.value = true;
    }
  }

  private checkRansomwareMitigationStateForAttack() {
    const ransomwareValue = this.onExecuteRansomwareEnableField.value;
    const localAttackValue = this.onExecuteRansomwareLocalAttackEnableField.value;
    const remoteAttackValue = this.onExecuteRansomwareRemoteAttackEnableField.value;

    // wait for component to be ready
    // nothing to do if already disabled
    if (!this.isReady || !ransomwareValue) {
      return;
    }

    // if ransomware is enabled but no attack is enabled, then disable ransomware by default
    if (!localAttackValue && !remoteAttackValue) {
      this.onExecuteRansomwareEnableField.value = false;
    }
  }

  private toggleRansomwareRemediationDisabledState() {
    const isVirusControlDisabled = !this.onExecuteVirusControlEnableField.value;
    const isRansomwareRemediationDisabled = !this.onExecuteRansomwareEnableField.value;

    toggleDisabled(this.onExecuteRansomwareEnableField, isVirusControlDisabled);
    toggleDisabled(this.onExecuteRansomwareContent, isVirusControlDisabled || isRansomwareRemediationDisabled);
    toggleDisabled(this.onExecuteRansomwareInfoText, isVirusControlDisabled || isRansomwareRemediationDisabled);
    toggleVisibility(this.onExecuteRansomwareInfoBox, isVirusControlDisabled);
  }

  private toggleOnExecuteFilelessDisabledState() {
    // field not rendered yet
    if (!this.onExecuteCommandLineFilelessEnableField) {
      return;
    }

    const isFilelessReadOnly = this.masks.includes(SectionMask.ANTIMALWARE_ON_ACCESS);

    toggleDisabled(this.onExecuteCommandLineFilelessEnableField, isFilelessReadOnly);
    toggleDisabled(
      this.onExecuteCommandLineContent,
      isFilelessReadOnly || !this.onExecuteCommandLineFilelessEnableField.value
    );
    toggleDisabled(
      this.onExecuteCommandLineInfoText,
      isFilelessReadOnly || !this.onExecuteCommandLineFilelessEnableField.value
    );
    toggleVisibility(this.onExecuteCommandLineInfoBox, isFilelessReadOnly);
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.antimalware.onExecute
   */
  protected getValueFromManager(): AntimalwareOnExecuteFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.ANTIMALWARE]: { [SectionId.ANTIMALWARE_ON_EXECUTE]: getAntimalwareOnExecuteDefaultValue() },
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.ANTIMALWARE]: {
          [SectionId.ANTIMALWARE_ON_EXECUTE]: PolicyPanelManager.getInstance().getPolicy(
            'uiSettings.antimalware.onExecute'
          ),
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        antimalware: {
          onExecute: {
            virusControl: {
              enable: this.onExecuteVirusControlEnableField,
              profile: this.onExecuteVirusControlProfileField,
              defaultAction: this.onExecuteVirusControlDefaultActionField,
              kernelApiMonitor: {
                enabled: this.onExecuteKernelApiEnableField,
              },
            },
            commandLineScanning: {
              filelessAttackProtection: this.onExecuteCommandLineFilelessEnableField,
              enable: this.onExecuteCommandLineEnableField,
              amsi: {
                enable: this.onExecuteCommandLineAmsiEnableField,
                reportDetectionsToCaller: this.onExecuteCommandLineAmsiReportField,
              },
            },
            ransomwareRemediation: {
              enable: this.onExecuteRansomwareEnableField,
              localAttack: {
                enable: this.onExecuteRansomwareLocalAttackEnableField,
              },
              remoteAttack: {
                enable: this.onExecuteRansomwareRemoteAttackEnableField,
              },
              restore: {
                mode: this.onExecuteRansomwareRestoreModeField,
              },
              efsProtection: {
                enabled: this.onExecuteRansomwareEfsEnableField,
              },
            },
            theta: {
              enabled: this.onExecuteThetaEnableField,
            },
            sensitiveRegistryProtection: {
              enable: this.onExecuteSensitiveProtectionEnableField,
              action: this.onExecuteSensitiveProtectionActionField,
            },
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected async setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        antimalware: {
          onExecute: {
            virusControl: {
              enable: this.valueT.uiSettings.antimalware.onExecute.virusControl.enable,
              profile: this.valueT.uiSettings.antimalware.onExecute.virusControl.profile,
              defaultAction: this.isCompanionStandalone
                ? AntimalwareOnExecuteVirusControlDefaultAction.REPORT_ONLY
                : this.valueT.uiSettings.antimalware.onExecute.virusControl.defaultAction,
              kernelApiMonitor: {
                enabled: this.valueT.uiSettings.antimalware.onExecute.virusControl.kernelApiMonitor.enabled,
              },
            },
            commandLineScanning: this.valueT.uiSettings.antimalware.onExecute.commandLineScanning,
            ransomwareRemediation: this.valueT.uiSettings.antimalware.onExecute.ransomwareRemediation,
            theta: this.valueT.uiSettings.antimalware.onExecute.theta,
            sensitiveRegistryProtection: this.valueT.uiSettings.antimalware.onExecute.sensitiveRegistryProtection,
          },
        },
      },
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    // use old value for default action if companion. Discard value from form group.
    if (this.isCompanionStandalone) {
      _.set(
        value,
        ['uiSettings', 'antimalware', 'onExecute', 'virusControl', 'defaultAction'],
        this.valueT?.uiSettings.antimalware.onExecute.virusControl.defaultAction
      );
    }

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'antimalware', 'onExecute'], {});
    }

    return value;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ON_EXECUTE: GzAntimalwareOnExecuteWebComponent;
  }
}
